<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { PropType } from 'vue'
import type { IBlog_Data } from '@/components/content-app/interface/blog/IBlog'
import type { ICategory } from '@/components/content-app/interface/blog/IBlogCategory'
import BlogsService from '@/services/content-app/blog.service'
import { useRoute, useRouter } from 'vue-router'

import { debounce } from 'lodash-es'

const isOpened = ref<boolean>(false)
const _BlogsService = new BlogsService()
const route = useRoute()
const router = useRouter()
const isDark = false
let searchData = ref<string>('')
let response = ref<IBlog_Data[]>([])
const searchSection = ref<HTMLDivElement | null>(null)

const props = defineProps({
  categories: {
    type: Object as PropType<ICategory[]>,
    required: true
  }
})

/**
 * navigate to single blog
 * @param url - blog url
 */
const navigateToSingleBlog = (url: string) => {
  router.push(url)
  isOpened.value = false
}

/**
 * desktop search
 */
const search = async () => {
  response.value = await _BlogsService.getSearchedBlogsForAutocomplete(searchData.value)
}

/**
 * mobile search
 */
const mobileSearch = async () => {
  isOpened.value = true
  search()
}

/**
 * clean search data
 */
const searchToggle = () => {
  isOpened.value = !isOpened.value
  searchData.value = ''
  response.value = []
}

/**
 *
 * @param date is string which come from backend
 * @returns string
 */
const formatDate = (date: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }

  const formattedDate = new Date(date).toLocaleDateString('en-US', options)
  return formattedDate
}

/**
 *
 * @param selector - id of item
 */
const scrollToElement = (selector: string) => {
  const targetElement = document.querySelector(selector)

  if (targetElement) {
    targetElement.scrollIntoView({ behavior: 'smooth' })
  }
}

const debouncedSearch = debounce(search, 500)

/**
 * Function to handle clicks outside the component
 * @param event -
 */
function handleClickOutside(event: any) {
  if (searchSection.value && !searchSection.value.contains(event.target)) {
    isOpened.value = false
  }
}

onMounted(() => {
  watch(searchData, (val) => {
    if (val === '') {
      searchToggle()
    } else {
      debouncedSearch()
    }
  })
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
<template>
  <div ref="searchSection" class="w-full md:w-max">
    <div class="hidden md:block">
      <button class="mr-5" @click="searchToggle">
        <FontAwesomeIcon icon="fa-magnifying-glass" class="w-4 text-text-brand" />
      </button>
    </div>
    <div class="relative w-full px-5 md:hidden">
      <input
        v-model="searchData"
        type="text"
        placeholder="Search"
        class="h-9 w-full rounded-lg px-2 outline-none"
      />
      <button @click="mobileSearch" class="absolute right-7 top-1/2 -translate-y-1/2">
        <FontAwesomeIcon icon="fa-magnifying-glass" class="w-4 text-text-brand" />
      </button>
    </div>
    <div
      :class="isOpened ? 'opacity-1 z-40' : '-z-50 opacity-0'"
      class="absolute right-0 top-[145px] w-full transition-all md:top-full"
    >
      <div class="relative mx-5 md:mx-11 xl:mx-44">
        <div
          class="relative z-50 hidden rounded-2xl border border-[#A6B6D2] bg-background-quinary px-5 py-2.5 md:flex dark:bg-[#25272C]"
        >
          <input
            v-model="searchData"
            class="w-4/5 bg-background-quinary pr-2.5 text-[#4B4B4B] outline-none placeholder:text-[#4B4B4B] dark:bg-[#25272C] dark:text-[#D8DBDF] dark:placeholder:text-[#D8DBDF]"
            placeholder="Enter the topic you want to search"
            type="text"
          />
          <button @click="search" class="btn btn-primary w-1/5">Search</button>
        </div>
        <div
          :class="response && response.length > 0 ? 'opacity-1 z-40' : '-z-40 opacity-0'"
          class="relative -mt-8 rounded-lg bg-white px-4 pt-5 shadow-[0_4px_14px_0_rgba(0,0,0,0.15)] transition-all sm:-mt-5 md:mx-2.5 dark:bg-[#25272C]"
        >
          <div
            class="flex items-start justify-between border-b border-[#C4C2C2] py-4 text-[#7C7979] dark:border-[#7C7979] dark:text-[#7C7979]"
          >
            TOPIC RESULTS:
            <FontAwesomeIcon
              @click="isOpened = false"
              icon="fa-xmark"
              class="cursor-pointer md:hidden"
            />
          </div>
          <div class="result-panel mt-3 max-h-56 overflow-y-auto">
            <div
              @click="navigateToSingleBlog(`/blog/${item.attributes.url}`)"
              v-for="(item, index) in response"
              :key="item.id"
              :class="{ 'border-t border-[#FD5200]': index > 0 }"
              class="block cursor-pointer py-3 text-sm text-[#747474]"
            >
              <p class="mb-1.5 text-black dark:text-[#D8DBDF]">
                {{ item.attributes.title }}
              </p>
              {{ formatDate(item.attributes.publishedAt) }}
            </div>
          </div>
          <div class="md:hidden">
            <p class="border-b border-[#C4C2C2] pb-2 text-xs dark:text-[#7C7979]">By Category</p>

            <div class="py-5" :class="isDark ? 'dark-navbar' : 'navbar'">
              <div
                class="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 px-5 text-[10px]"
                v-if="route.name === 'blog-home'"
              >
                <router-link to="/blog">All blogs</router-link>
                <a
                  v-for="item in props.categories"
                  :key="item.attributes.url"
                  :href="`#${item.attributes.url}`"
                  @click.prevent="scrollToElement(`#${item.attributes.url}`)"
                  >{{ item.attributes.title }}</a
                >
              </div>
              <div
                class="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 px-5 text-[10px]"
                v-else
              >
                <router-link to="/blog">All blogs</router-link>
                <router-link
                  v-for="item in props.categories"
                  :key="item.attributes.url"
                  :to="{ path: `/blog/category/${item.attributes.url}` }"
                >
                  {{ item.attributes.title }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.result-panel::-webkit-scrollbar {
  width: 5px;
}

.result-panel::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.result-panel::-webkit-scrollbar-thumb {
  background: #afafaf;
}

.result-panel::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.navbar a.router-link-exact-active {
  color: #fd5200;
}

.dark-navbar a.router-link-exact-active {
  color: white;
}

.dark-navbar a {
  color: #595959;
}
</style>
